import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch("https://formspree.io/f/mrbrgwnq", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        // ✅ Fire Google Analytics event on successful form submission
        if (window.gtag) {
          window.gtag('event', 'form_submit', {
            event_category: 'Contact',
            event_label: 'Contact Form Submitted'
          });
        }

        setIsSubmitted(true);
        setFormData({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => setIsSubmitted(false), 5000);
      } else {
        alert("There was an issue submitting your message. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Something went wrong. Please try again later.");
    }

    setIsSubmitting(false);
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <div className="contact-hero-content text-center fade-in">
            <h1 className="display-medium mb-lg">Let's Start Your Transformation</h1>
            <p className="body-large text-gray" style={{ maxWidth: '720px', margin: '0 auto' }}>
              Ready to begin your transformation? Let's make your goals a reality. Fill out the form below and we'll get back to you within 24 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="contact-content section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Information */}
            <div className="contact-info slide-in-left">
              <h2 className="heading-2 mb-lg">Get In Touch</h2>
              <p className="body-medium text-gray mb-xl">
                Whether you're ready to start your journey or just have questions, we're here to help. Reach out and let's discuss how we can help you achieve your fitness goals.
              </p>
              
              <div className="contact-details">
                <div className="contact-detail-item">
                  <div className="contact-icon">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h4 className="heading-3 mb-sm">Email Us</h4>
                    <a href="mailto:help@sympozium.life" className="body-medium contact-link">
                      help@sympozium.life
                    </a>
                  </div>
                </div>
                
                <div className="contact-detail-item">
                  <div className="contact-icon">
                    <Phone size={24} />
                  </div>
                  <div>
                    <h4 className="heading-3 mb-sm">Call Us</h4>
                    <a href="tel:+917396794767" className="body-medium contact-link">
                      +91 7396794767
                    </a>
                    <p className="body-small text-gray">Everyday: 10am - 10pm</p>
                  </div>
                </div>
                
                <div className="contact-detail-item">
                  <div className="contact-icon">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h4 className="heading-3 mb-sm">Visit Us</h4>
                    <p className="body-medium">New Mallepally</p>
                    <p className="body-medium">Hyderabad 500001, India</p>
                  </div>
                </div>
              </div>
              
              <div className="contact-hours mt-xl">
                <h4 className="heading-3 mb-md">Studio Hours</h4>
                <div className="hours-list">
                  <div className="hours-item">
                    <span className="body-medium">Everyday</span>
                    <span className="body-medium text-gray">10:00 AM - 10:00 PM</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-wrapper slide-in-right">
              {isSubmitted && (
                <div className="success-message fade-in">
                  <CheckCircle size={24} />
                  <div>
                    <h4 className="heading-3">Message Sent Successfully!</h4>
                    <p className="body-small">We'll get back to you within 24 hours.</p>
                  </div>
                </div>
              )}
              
              <form className="contact-form" onSubmit={handleSubmit}>
                <h3 className="heading-2 mb-lg">Send Us A Message</h3>
                
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="form-input"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="form-input"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="form-input"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="message" className="form-label">Your Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    className="form-textarea"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    placeholder="Tell us about your fitness goals and how we can help..."
                  ></textarea>
                </div>
                
                <button 
                  type="submit" 
                  className="btn btn-primary btn-large" 
                  style={{ width: '100%' }} 
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send size={20} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section section">
        <div className="container">
          <div className="map-wrapper">
            <div className="map-placeholder">
              <MapPin size={48} />
              <h3 className="heading-3 mt-md">Studio Location</h3>
              <p className="body-medium text-gray">New Mallepally, Hyderabad 500001, India</p>
              <a 
                href="https://maps.google.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-outline-red mt-lg"
              >
                Get Directions
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
